import {gql} from '@apollo/client';

export const GET_ALL_COLLECTIONS = gql`
  query AdminGetAllCollections($filters: CollectionFilters) {
    adminGetAllCollections(filters: $filters) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithAdminCollections {
        message
        data {
          collections {
            id
            iconUrl
            name
            ticker
            industry {
              name
            }
            exchange {
              id
              code
              stockExchangeName
            }
            # Instrument v2 count. The legacy companies array is still
            # resolved from the constituents' issuers, but is no longer
            # the truth and must never be sent back on a write.
            instrumentCount
            # adminGetAllCollections does not reliably exclude soft-deleted
            # records, so the table filters on this itself.
            isDeleted
            status
            announcedAt
            createdAt
          }
          pagination {
            page
            limit
            pages
            total
            hasPrevPage
            hasNextPage
            previous
            next
          }
          collectionOrders
        }
      }
    }
  }
`;

export const GET_COLLECTION_STATS = gql`
  query AdminGetCollectionStats($collectionId: ID!) {
    adminGetCollectionStats(collectionId: $collectionId) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithAdminCollectionStats {
        message
        data {
          totalOrders
          totalCompanies
          totalInvested
          totalUsers
          id
          name
        }
      }
    }
  }
`;

/* ---------------------------------------------------------------------------
   Instrument v2. A collection constituent is a PublicEquity financial
   instrument, not a company.

   `members` is the one place the configured unit rules live — `instruments`
   alone returns the instruments without min/default/max, so the edit form
   cannot rehydrate from it.

   `companies` is still resolved (derived from the constituents' issuers) but
   is legacy read-only; it must never be sent back on a write.
   --------------------------------------------------------------------------- */
export const GET_COLLECTION_BY_ID = gql`
  query GetCollection($collectionId: ID!) {
    getCollection(collectionId: $collectionId) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithCollection {
        data {
          id
          name
          language
          description
          bannerUrl
          videoUrl
          ticker
          iconUrl
          linkedCollectionGroups {
            id
            title
          }
          industry {
            id
            name
          }
          currency
          stockExchange
          exchange {
            id
            code
            stockExchangeName
            logoImage
            baseCurrencyCode
          }
          members {
            instrumentId
            minimumUnits
            defaultUnits
            maximumUnits
            instrument {
              id
              name
              ticker
              icon
            }
          }
          allowUserRemoveInstrument
          tradeable
          minInstrumentCount
          orderType
          dabaScore {
            score
            pdfReport
            business
            industry
            market
            team
            strategyProduct
            accelerator
            riskChallenges
            analogus
            economicsAndFinance
            legalCompliance
            returnOutlook
            socialImpact
          }
          financialReturns {
            YTD
            today
          }
          status
          announcedAt
          multilang {
            name
            description
            bannerUrl
            videoUrl
            pdfReport
            language
          }
        }
      }
    }
  }
`;

/* Constituent picker. Scoped by exchange code because the resolver rejects any
   instrument not listed on the collection's exchange. `latestQuote` decides
   whether a row is selectable — an instrument without one is rejected too. */
export const GET_EXCHANGE_LISTED_INSTRUMENTS = gql`
  query AdminCollectionInstruments(
    $exchangeCode: STOCK_EXCHANGE_CODE!
    $search: String
    $page: Int
    $limit: Int
  ) {
    adminGetStockExchangeListedInstruments(
      stockExchangeCode: $exchangeCode
      searchTerm: $search
      page: $page
      limit: $limit
    ) {
      ... on Error {
        message
        statusCode
      }
      ... on StockExchangeInstrumentList {
        page
        limit
        pages
        total
        hasNextPage
        instruments {
          id
          name
          ticker
          icon
          exchange {
            id
            code
            stockExchangeName
          }
          latestQuote {
            lastPrice
            baseCurrencyCode
            asOf
          }
        }
      }
    }
  }
`;

export const GET_COLLECTION_ORDERS = gql`
  query GetOrders($input: OrderFilter) {
    getOrders(input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithOrders {
        message
        data {
          user {
            lastName
            id
            firstName
            imageUrl
          }
          createdAt
          quantity
          fillValue
          price
        }
      }
    }
  }
`;

export const GET_ALL_COLLECTION_GROUPS = gql`
  query GetAllCollectionGroups($filters: CollectionGroupFilters) {
    getAllCollectionGroups(filters: $filters) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithCollectionGroups {
        data {
          pagination {
            total
          }
          collectionGroups {
            id
            title
            language
            description
            imageUrl
            hasParentGroup
            isVisible
            collectionCount
            collections {
              id
              ticker
              name
            }
            linkedCollectionGroups {
              id
              title
            }
            isDeleted
            createdAt
            deletedAt
          }
        }
      }
    }
  }
`;

export const GET_COLLECTION_ICONS = gql`
  query Error {
    getAllCollectionIcons {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithCollectionIcons {
        message
        data {
          id
          name
          url
        }
      }
    }
  }
`;

export const GET_COLLECTION_GROUP = gql`
  query GetCollectionGroup($collectionGroupId: ID!) {
    getCollectionGroup(collectionGroupId: $collectionGroupId) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithCollectionGroup {
        data {
          id
          title
          language
          description
          collections {
            id
          }
          imageUrl
          linkedCollectionGroups {
            id
          }
          hasParentGroup
          isVisible
          collectionCount
          multilang {
            title
            description
            language
          }
          isDeleted
          createdAt
          deletedAt
        }
      }
    }
  }
`;
